import FormikAutocompleteField from '@components/Inputs/FormikAutocompleteField';
import { Grid } from '@mui/material';
import { Field, useField } from 'formik';
import { isEmpty, sortBy } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { IAtomicCondition, IFlowConditions } from 'types/flows';
import { FormikSelectField, FormikTextField } from '../../../../Inputs';
import { getFieldPathIndices } from '../helpers';
import { AtomicChangeInitType, ConditionChangeCallback } from '../types';
import { conditionKeys, operatorsOrder } from './constants';

type Option = {
	value: string;
	label: string;
};
interface IAtomicConditionProps {
	formikName: string;
	conditions: IFlowConditions;
	atomicCondition: IAtomicCondition;
	onConditionChange?: ConditionChangeCallback;
}

const getInputType = (type?: string) => {
	switch (type) {
		case 'number':
			return 'number';
		default:
			return 'string';
	}
};

const AtomicCondition = ({
	formikName,
	conditions,
	atomicCondition,
	onConditionChange
}: IAtomicConditionProps) => {
	const { operator, target } = atomicCondition;
	const condition = conditions[operator];
	const multiple = operator === 'in' || operator === 'not-in';

	const [, , helpers] = useField({ name: `${formikName}.operator` });

	const currentKeyType = conditionKeys.find(
		({ name }) => name === target.value
	)?.type;

	const operatorOptions = useMemo(
		() =>
			Object.values(conditions)
				.filter(
					({ applicableOn }) =>
						!currentKeyType || applicableOn?.includes(currentKeyType!)
				)
				.sort((a, b) => {
					const aIndex = operatorsOrder.findIndex((op) => op === a.ID);
					const bIndex = operatorsOrder.findIndex((op) => op === b.ID);

					return aIndex - bIndex;
				})
				.map(({ ID, name }) => ({ value: ID, label: name })),
		[conditions, currentKeyType]
	);

	const keyOptions = useMemo(
		() =>
			sortBy(
				conditionKeys.map(({ name, options }) => ({
					value: name,
					label: name,
					description: `atomicConditions.description.${name}`,
					options
				})),
				'label'
			),
		[]
	);

	const selectedKeyRH = useMemo(
		() =>
			keyOptions
				.find((ko) => ko.label === target.value)
				?.options?.map((option) => ({
					value: option.label,
					label:
						(option.display && intl.tr(option.display)) ||
						option.value ||
						option.label
				})),
		[keyOptions, target]
	);

	useEffect(() => {
		if (
			target.value &&
			operatorOptions.length > 0 &&
			!operatorOptions.find((option) => option.value === operator)
		)
			helpers.setValue(operatorOptions[0].value);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [operator, operatorOptions, currentKeyType, target.value]);

	let rh: React.ReactNode | null = (
		<FormikTextField
			name={`${formikName}.predicate.value`}
			size="small"
			required
			fullWidth
			type={getInputType(currentKeyType)}
			label={intl.tr(
				'add.condition.task.dialog.conditions.condition.predicate'
			)}
			data-testid="atomic-condition-rhv-f"
		/>
	);

	if (condition?.targetOnly) {
		rh = null;
	} else if (multiple || !isEmpty(selectedKeyRH)) {
		rh = (
			<Field
				blurOnSelect
				component={FormikAutocompleteField}
				name={`${formikName}.predicate.value`}
				size="small"
				multiple={multiple}
				freeSolo={isEmpty(selectedKeyRH)}
				disableClearable
				options={selectedKeyRH || []}
				label={intl.tr(
					'add.condition.task.dialog.conditions.condition.predicate'
				)}
				required
				data-testid="atomic-condition-rhv-s"
				isOptionEqualToValue={(option: Option, value: string | Option) =>
					option.value === ((value as Option).value || value)
				}
			/>
		);
	}

	return (
		<Grid item container spacing={2} pt={2} alignItems="baseline">
			<Grid item xs={4.5}>
				<Field
					blurOnSelect
					component={FormikAutocompleteField}
					name={`${formikName}.target.value`}
					size="small"
					disableClearable
					options={keyOptions}
					label={intl.tr(
						'add.condition.task.dialog.conditions.condition.target'
					)}
					required
					data-testid="atomic-condition-key"
					isOptionEqualToValue={(option: Option, value: string | Option) =>
						option.value === ((value as Option).value || value)
					}
					onConditionChange={(value: string) => {
						if (onConditionChange) {
							const { taskIndex, condIndex } = getFieldPathIndices(formikName);
							if (taskIndex !== undefined && condIndex !== undefined) {
								onConditionChange({
									type: AtomicChangeInitType.target,
									val: value,
									taskIndex,
									condIndex
								});
							}
						}
					}}
				/>
			</Grid>
			<Grid item xs={3}>
				<FormikSelectField
					required
					name={`${formikName}.operator`}
					options={operatorOptions}
					onConditionChange={(value: string) => {
						if (onConditionChange) {
							const { taskIndex, condIndex } = getFieldPathIndices(formikName);
							if (taskIndex !== undefined && condIndex !== undefined) {
								onConditionChange({
									type: AtomicChangeInitType.operator,
									val: value,
									taskIndex,
									condIndex
								});
							}
						}
					}}
					data-testid="atomic-condition-operator"
				/>
			</Grid>
			<Grid item xs={4.5}>
				{rh}
			</Grid>
		</Grid>
	);
};

AtomicCondition.defaultProps = {
	onConditionChange: () => {}
};

export default AtomicCondition;
